<script setup lang="ts">
import ModalModelPartsVideo from "@/components/Modais/ModalModelPartsVideo.vue";
import ModalMessages from "@/components/Modais/ModalMessages.vue";
import api from "@/api";
import translateModel from "@/tradutors/model";
import VuePlyr from "vue-plyr";
import "vue-plyr/dist/vue-plyr.css";
import { gtmViewVideo, gtmPlayVideo, gtmFinishVideo } from "~/util/gtm-videos";
import { isMobile } from "~/shared/utils/mobile";
import {
  videoPlayerTools,
  type ControleDoVideo,
} from "@/composables/webmcp/tools.media";

const storeUser = userStore();
const route = useRoute();
// `somentePublico`: só a casca do SSR chegou (partes, nomes, pôster), sem URL
// de reprodução; a resposta do cliente remonta o componente.
// `aguardandoVersao`: a resposta do cliente ainda não chegou (vale também no
// HTML do SSR, que não sabe quem é o usuário); o convite de prévia, que é do
// anônimo, fica escondido e o pôster mostra carregamento.
const props = defineProps([
  "model",
  "videosModel",
  "somentePublico",
  "aguardandoVersao",
]);
const emit = defineEmits(["plans"]);
const translate = computed(() =>
  route.params.language == "pt-br"
    ? translateModel.portugues
    : translateModel.ingles,
);
const reactiveLocale = computed(() => route.params.language);
const reativeIsLoggedIn = computed(() => storeUser.isLoggedIn);
const preview = ref<any>(null);
interface VideoPath {
  id: string;
  videoPath: string;
  name: string;
}
// Só vídeo com URL de reprodução vira player; sem ela, fica o pôster.
function primeiroReproduzivel(parte: any): VideoPath | false {
  const video = parte?.videos?.[0];
  return video?.videoPath ? video : false;
}

const name = ref(props.model.nameModel);
// a API de metadados manda `descriptionModel`; `description` não existe nela
const description = ref(props.model.descriptionModel ?? props.model.description);
const text = ref(props.model.text);
const sessionIndex = ref(0);
const shoots = ref<any>(props.videosModel.shoot);
const videoSelect = ref<VideoPath | false>(
  primeiroReproduzivel(props.videosModel.shoot[0]),
);
const selectResolution = ref("360p");
const showPartsVideo = ref(false);
const videos = ref<any>(props.videosModel.shoot[0].videos);
const partId = ref(props.videosModel.shoot[0].id);
const msgSubscribeToWatch = ref(
  reactiveLocale.value == "pt-br"
    ? "Clique aqui e veja uma prévia"
    : "Click here to see a preview",
);
const showMsgpreview = ref(true);
const videoSource = ref({
  type: "video",
  sources: [
    {
      src: videoSelect.value ? videoSelect.value.videoPath : "",
      type: "video/mp4",
    },
  ],
});
const options = ref({
  showModal: false,
  titleModal: "",
  textBodyModal: "",
  textButtonModal: "",
  routerName: "",
  functionOptions: null,
  showClose: true,
});
const plyrOptions = ref({
  controls: [
    "play-large", // The large play button in the center
    "restart", // Restart playback
    "rewind", // Rewind by the seek time (default 10 seconds)
    "play", // Play/pause playback
    "fast-forward", // Fast forward by the seek time (default 10 seconds)
    "progress", // The progress bar and scrubber for playback and buffering
    "current-time", // The current time of playback
    "duration", // The full duration of the media
    "mute", // Toggle mute
    "volume", // Volume control
    "captions", // Toggle captions
    "settings",
  ],
  fullscreen: {
    enabled: true,
    iosNative: true,
  },
  settings: ["quality", "speed"],
  debug: false,
  ratio: "16:9",
  quality: {
    options: [],
    forced: true,
    default: 360,
  },
});

onMounted(() => {
  if (props.videosModel) {
    getModel();
  }
});

watch(
  () => props.videosModel,
  () => {
    getModel();
  },
);

watch(reactiveLocale, (value) => {
  msgSubscribeToWatch.value =
    value == "pt-br"
      ? "Clique aqui e veja uma prévia"
      : "Click here to see a preview";
});

watch(sessionIndex, (value) => {
  showMsgpreview.value = false;
  videos.value = props.videosModel.shoot[value].videos;
  partId.value = props.videosModel.shoot[value].id;
  videoSelect.value = primeiroReproduzivel(props.videosModel.shoot[value]);
  videoSource.value.sources[0].src = videoSelect.value
    ? videoSelect.value.videoPath
    : "";
  setTimeout(() => {
    if (videoSelect.value && preview.value) {
      preview.value.player.source = videoSource.value;
      preview.value.player.poster = props.videosModel.shoot[value].partThumb;
      showMsgpreview.value = true;
    }
  }, 500);
});

watch(preview, (value) => {
  if (value) {
    gtmViewVideo("video", "previa", route.params.name as string);
    value.player.source = videoSource.value;
    value.player.on("ended", () => {
      gtmFinishVideo("video", "previa", route.params.name as string);
      handleEnded();
      showMsgpreview.value = true;
    });
    value.player.on("pause", () => {
      handlePause();
      showMsgpreview.value = true;
    });
    value.player.on("play", () => {
      gtmPlayVideo("video", "previa", route.params.name as string);
      setTimeout(() => {
        showMsgpreview.value = false;
      }, 10000);
    });
  }
});

function getModel() {
  // a casca do SSR não tem player; a mensagem de prévia fica até a remontagem
  if (props.somentePublico) return;
  videoSource.value.sources[0].src = videoSelect.value
    ? videoSelect.value.videoPath
    : "";
  setTimeout(() => {
    if (videoSelect.value && preview.value) {
      preview.value.player.source = videoSource.value;
      preview.value.player.poster =
        props.videosModel.shoot[sessionIndex.value].partThumb;
      showMsgpreview.value = true;
    } else {
      showMsgpreview.value = false;
    }
  }, 500);
}
function playVideo() {
  preview.value?.player.play();
}
function showModalMessages(data: any) {
  options.value.showModal = data.message.show;
  options.value.titleModal = data.message.title;
  options.value.textBodyModal = data.message.textBoby;
  options.value.textButtonModal = data.message.textButton;
  options.value.routerName = data.message.routerName;
  options.value.functionOptions = data.message.functionOptions;
  options.value.showClose = data.message.showClose;
}
function showModalModelPartsVideo(value: boolean) {
  showPartsVideo.value = value;
}
function changeResolutionMobile(resolution: number) {
  videoSelect.value = videos.value[resolution];
  selectResolution.value = videos.value[resolution].name;
}
function modalConfirmDownload(id: number) {
  showModalMessages({
    message: {
      show: true,
      title:
        reactiveLocale.value == "en"
          ? "Click CONFIRM DOWNLOAD to confirm that you want to download the video"
          : "Clique em CONFIRMAR DOWNLOAD para confirmar que você deseja baixar o vídeo",
      textButton:
        reactiveLocale.value == "en"
          ? "CONFIRM DOWNLOAD"
          : "CONFIRMAR DOWNLOAD",
      textBoby:
        reactiveLocale.value == "en"
          ? "This video will use 1 credit from your account"
          : "Este vídeo utilizará 1 crédito da sua conta",
      functionOptions: { name: "solicitaDownload", parameter: id },
      showClose: true,
    },
  });
}
const handlePause = () => {
  emit("plans", true);
};

const handleEnded = () => {
  emit("plans", true);
};
async function solicitaDownload(id: number) {
  api
    .get(
      "v1/videoscredits/solicitaDownload/video/" +
        id +
        `?locale=${reactiveLocale.value ?? route.params.locale ?? "pt-br"}`,
    )
    .then(({ data }) => {
      if (!data.error)
        showModalMessages({
          message: {
            show: true,
            title:
              reactiveLocale.value == "en"
                ? "Your download is being processed!"
                : "Seu download está sendo processado!",
            textButton: reactiveLocale.value == "en" ? "Close" : "Fechar",
            textBoby: data.message,
          },
        });
      else if (
        data.message.includes("Adquira créditos para realizar o download") ||
        data.message.includes("Purchase credits in order to download")
      ) {
        showModalMessages({
          message: {
            show: true,
            title:
              reactiveLocale.value == "en"
                ? "Error your download!"
                : "Falha ao solicitar download!",
            textButton:
              reactiveLocale.value == "en" ? "BUY CREDITS" : "COMPRAR CRÉDITOS",
            textBoby: data.message,
            routerName: "language-assine",
          },
        });
      } else {
        showModalMessages({
          message: {
            show: true,
            title:
              reactiveLocale.value == "en"
                ? "Error your download!"
                : "Falha ao solicitar download!",
            textButton: reactiveLocale.value == "en" ? "Close" : "Fechar",
            textBoby: data.message,
          },
        });
      }
    })
    .catch((err) => {
      showModalMessages({
        message: {
          show: true,
          title:
            reactiveLocale.value == "en"
              ? "Error your download!"
              : "Falha ao solicitar download!",
          textButton: reactiveLocale.value == "en" ? "Close" : "Fechar",
          textBoby: err.response.data.message,
        },
      });
    });
}

function changePart(index: number) {
  sessionIndex.value = index;
  window.scrollTo({ top: 0, behavior: "smooth" });
}

// WebMCP: as mesmas control_video e choose_video do player completo
// (Videos.vue), aqui sobre a prévia. Qualidade não se escolhe na prévia, e
// nenhuma das duas abre a janela de planos.
const controleDoVideo: ControleDoVideo = {
  tipo: "video",
  player: () => preview.value?.player ?? null,
  estado: () => ({
    previa: true,
    parte: sessionIndex.value,
    partes: shoots.value.map((shoot: any) => ({
      nome: String(shoot.name ?? ""),
      temVideo: Boolean(primeiroReproduzivel(shoot)),
    })),
    qualidade: null,
    qualidades: [],
  }),
  async escolherParte(indice) {
    const shoot = shoots.value[indice];
    if (!shoot) return "parte-inexistente";
    if (!primeiroReproduzivel(shoot)) return "parte-sem-video";
    sessionIndex.value = indice;
    // O watch do sessionIndex só troca a fonte do player 500 ms depois: a
    // espera deixa um play logo em seguida pegar a prévia da parte nova.
    await new Promise((resolve) => setTimeout(resolve, 600));
    return "ok";
  },
  escolherQualidade: async () => "so-previa",
};
useWebMcpTools((ctx) => videoPlayerTools(ctx, controleDoVideo));
</script>
<template>
  <div class="block block--white mt-3">
    <div class="d-flex">
      <div class="col-lg-5 model-data">
        <div class="title model-info">
          {{ name }}
          <span>Videos</span>
        </div>
        <h3 class="description mt-3 model-info">
          {{ description }}
        </h3>
        <p class="ml-5 mt-3 text model-info">
          {{ text }}
        </p>
      </div>
      <div class="col-lg-7 col-12">
        <div class="col-12">
          <div
            v-if="showMsgpreview && !aguardandoVersao"
            @click="playVideo()"
            class="d-flex justify-content-center title-msg"
          >
            {{ msgSubscribeToWatch }}
          </div>
          <div class="card-pagination pr-3 cursor-pointer">
            <div class="preview" v-if="videoSelect">
              <ClientOnly>
                <vue-plyr
                  ref="preview"
                  @pause="handlePause"
                  @ended="handleEnded"
                  :options="plyrOptions"
                  :source="videoSource"
                >
                  <video :autoplay="false" controls playsinline>
                    <source src="" type="video/mp4" />
                  </video>
                </vue-plyr>
              </ClientOnly>
            </div>
            <!-- Antes da resposta do cliente o clique não abre os planos: quem
                 assina ainda vai receber o player -->
            <div
              v-else
              class="img-box"
              @click="!somentePublico && emit('plans', true)"
            >
              <!-- elemento LCP do /video: o pôster vem do domínio principal,
                   pela Cloudflare (ver posterNoDominioPrincipal) -->
              <img
                v-if="shoots[sessionIndex]?.partThumb"
                :src="shoots[sessionIndex].partThumb"
                :alt="`${name} - ${shoots[sessionIndex].name}`"
                width="1280"
                height="720"
                fetchpriority="high"
              />
              <div class="play-icon">
                <div v-if="aguardandoVersao" class="spinner-border" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
                <font-awesome v-else :icon="['fas', 'circle-play']" size="2xl" />
              </div>
            </div>
          </div>
        </div>
        <div class="row buttons-video bg-white py-2 px-4">
          <div
            class="parts col-lg-2 col-11 d-flex justify-content-center align-items-center"
          >
            <select
              class="form-select py-2"
              aria-label="Default-select-parts"
              v-model="sessionIndex"
            >
              <option selected disabled>{{ translate.sessions }}</option>
              <option
                :value="index"
                v-for="(shoot, index) in shoots"
                :key="shoot.name"
              >
                {{ shoot.name }}
              </option>
            </select>
          </div>
          <div
            class="col-lg-2 col-1 d-flex justify-content-center align-items-center mobile-download"
            @click="
              reativeIsLoggedIn
                ? modalConfirmDownload(partId)
                : emit('plans', true)
            "
            v-if="videoSelect"
          >
            <button
              type="button"
              class="btn btn-outline-secondary btn-lg px-2 btn-desktop"
            >
              <span class="video-options">{{ translate.download }}</span>
              <font-awesome
                :icon="['fas', 'download']"
                size="md"
                style="color: #b20000; margin-left: 3px"
              />
            </button>
            <font-awesome
              :icon="['fas', 'download']"
              size="md"
              style="color: #b20000; margin-left: 3px"
            />
          </div>
        </div>
        <div class="row d-flex px-2">
          <div
            class="cursor-pointer col-xl-3 col-6"
            v-for="(shoot, index) in shoots"
            :key="shoot.name"
          >
            <template v-if="shoot.videos.length">
              <div
                class="d-flex align-items-center justify-content-center title-part"
                @click="changePart(index)"
              >
                <p :class="['text1', { 'text1-active': sessionIndex === index }]">{{ shoot.name }}</p>
                <span :class="['dropdown-toggle', { 'dropdown-toggle-right': sessionIndex !== index }]"></span>
              </div>
              <div
                v-if="!aguardandoVersao"
                @click="changePart(index)"
                class="title-msg-2"
              >
                {{ msgSubscribeToWatch }}
              </div>
              <div
                @click="changePart(index)"
                class="box-live"
                :style="`background-image: url(${shoot.partThumb});background-size: cover;background-repeat: no-repeat;background-position: 50% 20%;`"
              ></div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
  <ClientOnly>
    <ModalModelPartsVideo
      @showModalModelPartsVideo="showModalModelPartsVideo"
      :showPartsVideo="showPartsVideo"
      :parts="shoots"
      :translate="translate"
      @changeResolution="changeResolutionMobile"
    />
    <ModalMessages
      @showModalMessages="showModalMessages"
      @solicitaDownload="solicitaDownload"
      :options="options"
      :width="isMobile() ? '95%' : '600px'"
    />
  </ClientOnly>
</template>
<style scoped lang="css">
:deep(.plyr video) {
  width: 100%;
  object-fit: contain;
}

:deep(.plyr:fullscreen video) {
  height: 100vh !important;
  width: 100vw !important;
  object-fit: contain;
}

.block {
  padding: 28px 0;
}

#model-video-box {
  width: 827px;
  height: 465px;
}

.model-info {
  padding-left: 137px;
}

h3 h1 p {
  font-family: "Exo-2";
}

.name-video {
  font-family: "Exo-2";
  font-size: 12px;
  font-weight: bold;
  color: #b20000;
}

.title {
  color: #000000;
  font-family: "Bebas-Neue";
  font-size: 72px;
  font-weight: bold;
  letter-spacing: 0;
  line-height: 72px;
}

.description {
  color: #b20000;
  font-family: "Roboto-Medium";
  font-size: 24px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 28px;
}

.text {
  height: 76px;
  color: #5b5858;
  font-family: "Roboto-Regular";
  font-size: 16px;
  letter-spacing: 0;
  line-height: 19px;
  max-width: 370px;
}

.video-options {
  height: 19px;
  font-family: "Exo-2";
  font-size: 15px;
  letter-spacing: 0;
  line-height: 19px;
}

.row .buttons-video > .col,
.row > [class*="col-"] {
  padding-right: 5px;
  padding-left: 5px;
}

.btn-outline-dark {
  font-size: 14px;
  font-weight: 500;
  border-radius: 30px;
}

.btn-outline-secondary {
  font-size: 14px;
  font-weight: 600;
  border-radius: 30px;
  border: none;
}

.parts > .form-select {
  background-color: #fff;
  color: #868686;
  font-size: 14px;
  font-weight: 600;
  font-family: "Exo-2";
  border: none;
  background-image: url("");
  --bs-form-select-bg-img: url("");
  pointer-events: none;
}

.form-select {
  background-color: #fff;
  color: #868686;
  font-size: 14px;
  font-weight: 600;
  font-family: "Exo-2";
  border: none;
  cursor: pointer;
  background-image: url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHZpZXdCb3g9JzAgMCAxNiAxNic+PHBhdGggZmlsbD0nbm9uZScgc3Ryb2tlPScjYjIwMDAwJyBzdHJva2UtbGluZWNhcD0ncm91bmQnIHN0cm9rZS1saW5lam9pbj0ncm91bmQnIHN0cm9rZS13aWR0aD0nMicgZD0nbTIgNSA2IDYgNi02Jy8+PC9zdmc+Cg==);
}

.form-select:hover {
  background-color: #6c757d;
  color: #fff;
  border-radius: 30px;
  border: none;
}

.img-box {
  position: relative;
  width: 100%;
}

.img-box img {
  width: 100%;
  height: 450px;
  filter: brightness(0.7);
  background-size: cover;
  object-fit: cover;
  background-position: center;
  object-position: center 25%;
}

.play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  /* Tamanho do ícone de reprodução */
  color: white;
  /* Cor do ícone de reprodução */
  cursor: pointer;
}

.mobile-bellacloud > svg,
.mobile-share > svg,
.mobile-download > svg,
.mobile-sections {
  display: none;
}

.model-info span {
  font-family: "Exo-2";
  font-size: 25px;
  line-height: 20px;
  display: block;
  width: 100%;
  font-family: "Bebas-Neue";
}

.title-msg {
  font-family: "Bebas-Neue";
  font-size: 40px;
  font-weight: 400;
  position: absolute;
  z-index: 99;
  color: #ffffff;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(255, 255, 255, 0.2);
  width: 100%;
  height: 80px;
  line-height: 90px;
  cursor: pointer;
}

.box-live {
  width: 100%;
  aspect-ratio: 16 / 9;
}

.dropdown-toggle:after {
  font-family: "fontello";
  font-size: 25px;
  content: "\e809";
  margin: 0;
  border: none;
  vertical-align: text-bottom;
  color: #b20000;
  margin-left: 0px;
}

.text1 {
  font-family: "Exo-2";
  color: #b5b5b5;
  font-size: 15px;
  line-height: 21px;
  font-weight: 600;
  margin-bottom: 5px;
  text-align: center;
}

.text1-active {
  font-weight: bold;
  color: #000;
}

.dropdown-toggle-right:after {
  content: "\e80c";
}

.title-part {
  margin-top: 20px;
}

.title-msg-2 {
  font-family: "Bebas-Neue";
  font-size: 17px;
  font-weight: 400;
  position: absolute;
  z-index: 99;
  color: #ffffff;
  top: 55%;
  left: 50%;
  transform: translate(-50%, 0%);
  background: rgba(255, 255, 255, 0.2);
  width: 100%;
  height: 40px;
  line-height: 45px;
  text-align: center;
}

@media screen and (max-width: 992px) {
  .model-data,
  .div-resolution,
  .btn-desktop {
    display: none;
  }

  .block {
    padding: 0px;
    margin-top: 0px !important;
  }

  .mobile-bellacloud > svg,
  .mobile-share > svg,
  .mobile-download > svg,
  .mobile-sections {
    display: block;
  }

  .row >>> #model-video-box {
    margin-bottom: 50px;
  }

  .buttons-video {
    box-shadow: -2px -3px 26px -12px rgba(0, 0, 0, 0.45);
    -webkit-box-shadow: -2px -3px 26px -12px rgba(0, 0, 0, 0.45);
    -moz-box-shadow: -2px -3px 26px -12px rgba(0, 0, 0, 0.45);
  }

  .name-video {
    font-family: "Exo-2";
    font-size: 14px;
    font-weight: bold;
    color: #000000;
  }

  #model-video-box {
    width: 100%;
    height: unset;
    right: unset;
    margin-left: 10px;
  }

  .img-box img {
    width: 992px;
    max-width: 97%;
    height: auto;
    aspect-ratio: 16 / 9;
    margin-left: 10px;
    filter: brightness(0.7);
    background-size: cover;
    object-fit: cover;
    background-position: center;
    object-position: center 25%;
  }

  .mobile-sections {
    top: -2px;
  }
}

@media screen and (max-width: 992px) {
  .title-msg {
    font-size: 25px;
    height: 50px;
    line-height: 55px;
    transform: translate(-50%, -50%);
  }
}
</style>
